/**
 * AuiTaskDashboard
 *
 * Self-resolving surface component that auto-discovers and renders all
 * delegated tasks for the current conversation. The agent just emits
 * { type: "TaskDashboard" } and the frontend handles everything:
 * real-time task discovery via WebSocket, cold-load via REST, layout,
 * sorting, and individual card rendering.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { AnimatePresence } from 'motion/react';
import { AnimatedListItem } from '../../ui/AnimatedListItem';
import { AuiDelegatedTaskCard } from './AuiDelegatedTaskCard';
import { TaskDashboardContext, type TaskDashboardContextValue } from './TaskDashboardContext';
import { usePlatformEvents } from '../../../contexts/PlatformEventContext';
import { useCanvases } from '../../../contexts/CanvasContext';
import { spaceApi } from '../../../services/api';
import {
  delegatedTaskStatusRegistry,
  delegatedTasksListCache,
  removeTaskFromListCache,
  trackDelegatedRun,
} from '../../../hooks/useDelegatedTaskStatus';
import { playCue } from '../../../utils/audio-theme';
import type { AuiRendererProps } from '../registry';
import type { AuiNode } from '../../../types/canvases';
import type { PlatformEvent, RunDelegatedData, RunDelegateCompletedData } from '../../../types/events';
import type { DelegatedTaskStatus, DelegatedTask } from '../../../types/delegated-tasks';

// ─── Grid layout constants ────────────────────────────────────────────

const COLS_CLASS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
};

const STATUS_PRIORITY: Record<DelegatedTaskStatus, number> = {
  pending: 0,
  running: 1,
  completed: 2,
  failed: 2,
  cancelled: 2,
};

// ─── Internal task entry ──────────────────────────────────────────────

interface TaskEntry {
  taskId: string;
  delegatedRunId: string;
  agentName: string;
  title: string;
  canvasId?: string;
  status?: DelegatedTaskStatus;
  updatedAt?: string;
  // Summary fields for terminal tasks
  durationMs?: number;
  toolCallCount?: number;
  usage?: { inputTokens: number; outputTokens: number; totalTokens: number };
  responsePreview?: string;
  error?: string;
}

// ─── Main component ───────────────────────────────────────────────────

export function AuiTaskDashboard({ node }: AuiRendererProps) {
  const props = node.component.props ?? {};
  const columns = Math.min(Math.max(typeof props.columns === 'number' ? props.columns : 2, 1), 4);
  const maxRows = typeof props.maxRows === 'number' ? props.maxRows : 4;
  const maxCards = columns * maxRows;

  const { subscribe, isConnected } = usePlatformEvents();
  const { conversationId, spaceId } = useCanvases();

  const [tasks, setTasks] = useState<Map<string, TaskEntry>>(new Map());
  const [loaded, setLoaded] = useState(false);
  const taskMapRef = useRef(tasks);
  taskMapRef.current = tasks;

  // Proactively subscribe to every discovered, non-terminal delegated run so
  // its live chain-of-thought accumulates in the shared cache from the run's
  // FIRST event — instead of only once a card mounts and subscribes (by which
  // point the early reasoning/tool events have already streamed past with no
  // listener, leaving the card to rely on REST list-polling for status). Cards
  // read this same cache via useDelegatedTaskStatus, so they show full live
  // progress regardless of mount timing. Read `subscribe` via a ref so the
  // reconcile runs on task changes only; PlatformEventContext re-syncs filters
  // across reconnects on its own.
  const subscribeRef = useRef(subscribe);
  subscribeRef.current = subscribe;
  const trackedRunsRef = useRef<Map<string, () => void>>(new Map());
  useEffect(() => {
    const tracked = trackedRunsRef.current;
    const wanted = new Set<string>();
    for (const t of tasks.values()) {
      const terminal =
        t.status === 'completed' || t.status === 'failed' || t.status === 'cancelled';
      if (t.delegatedRunId && !terminal) wanted.add(t.delegatedRunId);
    }
    for (const runId of wanted) {
      if (!tracked.has(runId)) tracked.set(runId, trackDelegatedRun(runId, subscribeRef.current));
    }
    for (const [runId, release] of tracked) {
      if (!wanted.has(runId)) {
        release();
        tracked.delete(runId);
      }
    }
  }, [tasks]);

  // Release all tracked subscriptions on unmount (conversation switch).
  useEffect(() => {
    const tracked = trackedRunsRef.current;
    return () => {
      for (const release of tracked.values()) release();
      tracked.clear();
    };
  }, []);

  // Cold-load: hydrate from cache (instant), then fetch in background.
  // Also schedules a delayed re-fetch to catch tasks created between
  // agent.run.delegated (which fires before this component mounts) and now.
  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;

    const mergeTasks = (apiTasks: DelegatedTask[]) => {
      if (cancelled) return;
      delegatedTasksListCache.set(conversationId, apiTasks);
      const map = new Map<string, TaskEntry>();
      for (const t of apiTasks) {
        if (!t.delegatedRunId) continue;
        map.set(t.taskId, taskFromApi(t));
      }
      setTasks((prev) => {
        // Merge: keep WS-discovered tasks that aren't in API yet
        const merged = new Map(map);
        for (const [id, entry] of prev) {
          if (!merged.has(id)) merged.set(id, entry);
        }
        return merged;
      });
    };

    // Hydrate from cache immediately so the grid isn't empty while we refetch.
    const cached = delegatedTasksListCache.get(conversationId);
    if (cached) {
      const map = new Map<string, TaskEntry>();
      for (const t of cached) {
        if (!t.delegatedRunId) continue;
        map.set(t.taskId, taskFromApi(t));
      }
      setTasks(map);
      setLoaded(true);
    }

    (spaceId ? spaceApi(spaceId).delegatedTasks.list(conversationId) : Promise.resolve([])).then((apiTasks) => {
      mergeTasks(apiTasks);
      setLoaded(true);
    }).catch(() => {
      setLoaded(true);
    });

    // Delayed re-fetch: the dashboard may mount after agent.run.delegated
    // has already fired (race condition). Re-fetch after 2s to catch any
    // tasks that were created just before mount.
    const timer = setTimeout(() => {
      (spaceId ? spaceApi(spaceId).delegatedTasks.list(conversationId) : Promise.resolve([])).then(mergeTasks).catch(() => {});
    }, 2000);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [conversationId]);

  // Re-fetch on reconnection to catch events missed during disconnect
  const wasConnectedRef = useRef(isConnected);
  useEffect(() => {
    const wasConnected = wasConnectedRef.current;
    wasConnectedRef.current = isConnected;

    // Only re-fetch on false → true transition (reconnection)
    if (!wasConnected && isConnected && conversationId) {
      (spaceId ? spaceApi(spaceId).delegatedTasks.list(conversationId) : Promise.resolve([])).then((apiTasks) => {
        delegatedTasksListCache.set(conversationId, apiTasks);
        const map = new Map<string, TaskEntry>();
        for (const t of apiTasks) {
          if (!t.delegatedRunId) continue;
          map.set(t.taskId, taskFromApi(t));
        }
        setTasks((prev) => {
          const merged = new Map(map);
          for (const [id, entry] of prev) {
            if (!merged.has(id)) merged.set(id, entry);
          }
          return merged;
        });
      }).catch(() => { /* silent — existing data remains */ });
    }
  }, [isConnected, conversationId]);

  // Real-time: subscribe to agent.run.* for task discovery and status updates.
  // We listen broadly because:
  // 1. agent.run.delegated — primary path for new task discovery
  // 2. agent.run.delegate_completed — terminal status update (has taskId)
  // 3. agent.run.completed/failed/cancelled — backup terminal status via runId match
  // 4. agent.run.tool_result (Delegate) — backup task discovery when
  //    agent.run.delegated fires before the dashboard mounts (race condition)
  useEffect(() => {
    const unsubscribe = subscribe(
      // Only `delegated` carries data.conversationId (RunDelegatedData) — scope
      // it server-side. `delegate_completed`/`tool_result` don't carry it, so
      // they stay unscoped and are matched by runId/taskId in the handler.
      [{ eventType: 'agent.run.delegated', conversationId }, { eventType: 'agent.run.delegate_completed' }, { eventType: 'agent.run.tool_result' }],
      (event: PlatformEvent) => {
        const eventType = event.eventType;
        const d = event.data as Record<string, unknown>;

        // ── 1. New task: agent.run.delegated ─────────────────────────
        if (eventType === 'agent.run.delegated') {
          const data = d as unknown as RunDelegatedData;
          if (import.meta.env.DEV) {
            console.debug('[TaskDashboard] received agent.run.delegated', data);
          }
          if (!data.taskId || !data.delegatedRunId) return;

          setTasks((prev) => {
            const next = new Map(prev);
            const existing = prev.get(data.taskId);
            next.set(data.taskId, {
              ...(existing ?? {}),
              taskId: data.taskId,
              delegatedRunId: data.delegatedRunId,
              agentName: data.agentName ?? existing?.agentName ?? 'Agent',
              title: data.title ?? existing?.title ?? '',
              canvasId: data.canvasId ?? existing?.canvasId,
              status: existing?.status ?? 'pending',
            });
            return next;
          });
          return;
        }

        // ── 2. Terminal status: agent.run.delegate_completed ─────────
        if (eventType === 'agent.run.delegate_completed') {
          const data = d as unknown as RunDelegateCompletedData;
          if (!data.taskId) return;
          const terminalStatus: DelegatedTaskStatus =
            data.status === 'failed' ? 'failed'
              : data.status === 'cancelled' ? 'cancelled'
                : data.success === false ? 'failed'
                  : 'completed';

          if (import.meta.env.DEV) {
            console.debug('[TaskDashboard] delegate_completed', data.taskId, terminalStatus);
          }

          setTasks((prev) => {
            const existing = prev.get(data.taskId);
            if (!existing) return prev;
            const next = new Map(prev);
            next.set(data.taskId, { ...existing, status: terminalStatus });
            return next;
          });
          return;
        }

        // ── 3. Backup discovery: Delegate tool_result ────────────────
        if (eventType === 'agent.run.tool_result') {
          const toolName = d.toolName as string | undefined;
          if (toolName !== 'Delegate') return;
          const output = d.output;
          if (!output) return;

          try {
            const parsed = typeof output === 'string' ? JSON.parse(output) : output;
            if (parsed?.status !== 'delegated' || !Array.isArray(parsed.tasks)) return;

            setTasks((prev) => {
              let next: Map<string, TaskEntry> | null = null;
              for (const t of parsed.tasks as Array<Record<string, unknown>>) {
                const taskId = t.taskId as string;
                const runId = t.runId as string;
                if (!taskId || !runId || prev.has(taskId)) continue;
                if (!next) next = new Map(prev);
                next.set(taskId, {
                  taskId,
                  delegatedRunId: runId,
                  agentName: (t.agentName as string) ?? 'Agent',
                  title: (t.title as string) ?? '',
                  canvasId: t.canvasId as string | undefined,
                  status: (t.status as DelegatedTaskStatus) ?? 'pending',
                });
              }
              return next ?? prev;
            });
          } catch {
            // Invalid JSON in tool output — ignore
          }
          return;
        }
      },
    );

    // Also subscribe to terminal run events to update dashboard status
    // when a known delegated run completes/fails/cancels.
    const unsubTerminal = subscribe(
      [{ eventType: 'agent.run.completed' }, { eventType: 'agent.run.failed' }, { eventType: 'agent.run.cancelled' }],
      (event: PlatformEvent) => {
        const d = event.data as Record<string, unknown>;
        const runId = d.runId as string | undefined;
        if (!runId) return;

        const terminalStatus: DelegatedTaskStatus =
          event.eventType === 'agent.run.failed' ? 'failed'
            : event.eventType === 'agent.run.cancelled' ? 'cancelled'
              : 'completed';

        setTasks((prev) => {
          // Reverse lookup: find task with this delegatedRunId
          for (const [taskId, entry] of prev) {
            if (entry.delegatedRunId === runId && entry.status !== terminalStatus) {
              const next = new Map(prev);
              next.set(taskId, { ...entry, status: terminalStatus });
              return next;
            }
          }
          return prev;
        });
      },
    );

    return () => { unsubscribe(); unsubTerminal(); };
  }, [subscribe, conversationId]);

  // Audio: ding once when a brand-new task arrives (size grows past the
  // baseline), and once when each task transitions to a terminal status.
  // Cold-loaded tasks (cache + initial fetch) are silently absorbed into
  // the baseline so reopening the dashboard doesn't replay the whole grid.
  const taskAudioBaselineRef = useRef<{
    knownTaskIds: Set<string>;
    completedFired: Set<string>;
    initialized: boolean;
  }>({ knownTaskIds: new Set(), completedFired: new Set(), initialized: false });

  useEffect(() => {
    const baseline = taskAudioBaselineRef.current;
    const terminalStatuses: ReadonlySet<DelegatedTaskStatus> = new Set([
      'completed',
      'failed',
      'cancelled',
    ]);

    if (!baseline.initialized) {
      // First non-empty render after mount = cold-load. Absorb everything
      // currently in the map without making sound.
      if (loaded || tasks.size > 0) {
        for (const [taskId, entry] of tasks) {
          baseline.knownTaskIds.add(taskId);
          if (entry.status && terminalStatuses.has(entry.status)) {
            baseline.completedFired.add(taskId);
          }
        }
        baseline.initialized = true;
      }
      return;
    }

    let newTaskFired = false;
    for (const [taskId, entry] of tasks) {
      if (!baseline.knownTaskIds.has(taskId)) {
        baseline.knownTaskIds.add(taskId);
        // Single ding even if multiple tasks land in the same tick.
        if (!newTaskFired) {
          playCue('task.created');
          newTaskFired = true;
        }
        // If the brand-new task is already terminal (e.g. arrived via
        // backup tool_result discovery after completion), record it as
        // completed-fired so we don't double-ding.
        if (entry.status && terminalStatuses.has(entry.status)) {
          baseline.completedFired.add(taskId);
        }
        continue;
      }
      if (
        entry.status
        && terminalStatuses.has(entry.status)
        && !baseline.completedFired.has(taskId)
      ) {
        baseline.completedFired.add(taskId);
        playCue('task.completed');
      }
    }
  }, [tasks, loaded]);

  const removeTask = useCallback((taskId: string) => {
    setTasks((prev) => {
      if (!prev.has(taskId)) return prev;
      const next = new Map(prev);
      next.delete(taskId);
      return next;
    });
    nodeCacheRef.current.delete(taskId);
    if (conversationId) removeTaskFromListCache(conversationId, taskId);
  }, [conversationId]);

  const dashboardContextValue = useMemo<TaskDashboardContextValue>(
    () => ({ removeTask }),
    [removeTask],
  );

  // Sort tasks: active first, then terminal. Cap at maxCards.
  const sortedTasks = useMemo(() => {
    const entries = [...tasks.values()].map((entry, index) => {
      // Check live status registry for accurate sorting
      const liveStatus = delegatedTaskStatusRegistry.get(entry.delegatedRunId);
      const effectiveStatus = liveStatus ?? entry.status ?? 'pending';
      return { entry, priority: STATUS_PRIORITY[effectiveStatus] ?? 2, index };
    });

    entries.sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      // Newest first within same priority group
      const aTime = a.entry.updatedAt ?? '';
      const bTime = b.entry.updatedAt ?? '';
      return bTime.localeCompare(aTime);
    });

    if (entries.length > maxCards) {
      const active = entries.filter((item) => item.priority < 2);
      const terminal = entries.filter((item) => item.priority >= 2);
      const terminalSlots = Math.max(0, maxCards - active.length);
      const keptTerminal = terminal.slice(-terminalSlots);
      return [...active, ...keptTerminal].map((item) => item.entry);
    }

    return entries.map((item) => item.entry);
  }, [tasks, maxCards]);

  // Build synthetic AuiNode for each task card, caching by taskId to avoid
  // unnecessary re-renders of memoized AuiDelegatedTaskCard components.
  const nodeCacheRef = useRef(new Map<string, { node: AuiNode; entry: TaskEntry }>());

  const cardNodes = useMemo(() => {
    const nextCache = new Map<string, { node: AuiNode; entry: TaskEntry }>();
    for (const entry of sortedTasks) {
      const cached = nodeCacheRef.current.get(entry.taskId);
      if (cached && cached.entry === entry) {
        nextCache.set(entry.taskId, cached);
      } else {
        const node: AuiNode = {
          id: `td-${entry.taskId}`,
          component: {
            type: 'DelegatedTaskCard',
            props: {
              taskId: entry.taskId,
              delegatedRunId: entry.delegatedRunId,
              agentName: entry.agentName,
              title: entry.title,
              status: entry.status,
              canvasId: entry.canvasId,
              updatedAt: entry.updatedAt,
              durationMs: entry.durationMs,
              toolCallCount: entry.toolCallCount,
              usage: entry.usage,
              responsePreview: entry.responsePreview,
              error: entry.error,
            },
          },
        };
        nextCache.set(entry.taskId, { node, entry });
      }
    }
    nodeCacheRef.current = nextCache;
    return nextCache;
  }, [sortedTasks]);

  if (!loaded && tasks.size === 0) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="aui-task-dashboard">
        {[0, 1].map((i) => (
          <div key={i} className="h-[140px] rounded-lg border bg-card animate-pulse" />
        ))}
      </div>
    );
  }

  if (tasks.size === 0) {
    return (
      <div data-testid="aui-task-dashboard" className="text-sm text-muted-foreground py-4 text-center">
        No tasks yet
      </div>
    );
  }

  return (
    <TaskDashboardContext.Provider value={dashboardContextValue}>
      <div data-testid="aui-task-dashboard">
        <div
          className={`grid ${COLS_CLASS[columns] ?? COLS_CLASS[2]}`}
          style={{ gap: '1rem' }}
        >
          <AnimatePresence initial={false}>
            {sortedTasks.map((entry) => (
              <AnimatedListItem key={entry.taskId}>
                <AuiDelegatedTaskCard node={cardNodes.get(entry.taskId)!.node} />
              </AnimatedListItem>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </TaskDashboardContext.Provider>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────

function taskFromApi(t: DelegatedTask): TaskEntry {
  return {
    taskId: t.taskId,
    delegatedRunId: t.delegatedRunId!,
    agentName: t.targetAgentName ?? 'Agent',
    title: t.title ?? '',
    canvasId: t.targetCanvasId,
    status: t.status,
    updatedAt: t.updatedAt,
    durationMs: t.summary?.durationMs ?? t.durationMs,
    toolCallCount: t.summary?.toolCallCount,
    usage: t.summary?.usage,
    responsePreview: t.summary?.responsePreview ?? t.response?.slice(0, 200),
    error: t.summary?.error ?? t.error,
  };
}
